import { IconChevronDown, IconCirclePlus } from '@tabler/icons-react-native';
import { StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { NewListingPrompt, useNewListingPrompt } from 'app/screens/new/prompt';
import { useTutorialRef } from 'app/components/tutorial-overlay';
import { Button } from 'app/design/primitives/button';
import { Box, Text, createTheme as createDSTheme } from 'app/shopify';
import { useAuthorize } from 'app/hooks/use-authorize';
import { useUser } from 'app/network/user';
import { TutorialRef } from 'app/types/tutorial';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useIsGuestWeb } from 'app/utils/web-helpers';
import { webTransition } from '../web-transition';

const theme = createDSTheme();

export function PublishButton() {
  const { t } = useTranslation('header');
  const { data: user } = useUser();
  const { viewRef: publishButtonRef } = useTutorialRef(TutorialRef.PublishButton);
  const isGuestWeb = useIsGuestWeb();

  const canPublish = isGuestWeb || isFeatureEnabled(FeatureFlag.publishListings, user?.featureFlags);

  const withAuthorize = useAuthorize();

  const { hideNewListingPrompt, showNewListingPrompt, newListingPromptState } = useNewListingPrompt();
  const [isPromptVisible] = newListingPromptState;

  const closePrompt = () => {
    hideNewListingPrompt();
  };

  const openPrompt = () => {
    if (isGuestWeb) {
      showNewListingPrompt('+');
      return;
    }
    withAuthorize(() => {
      showNewListingPrompt('+');
    });
  };

  const onButtonPress = () => {
    if (isPromptVisible) {
      closePrompt();
      return;
    }
    openPrompt();
  };

  if (!canPublish) {
    return null;
  }

  return (
    <Box ref={publishButtonRef} collapsable={false} position="relative" style={{ marginLeft: 'auto' }}>
      {isPromptVisible ? (
        <Button
          variant="plain"
          onPress={closePrompt}
          style={{
            ...StyleSheet.absoluteFill,
            position: 'fixed' as 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9,
          }}
        >
          <Box />
        </Button>
      ) : null}

      <Button
        onPress={onButtonPress}
        paddingHorizontal="spacing.general.x6"
        variant="primary"
        style={[
          {
            borderColor: 'rgba(0, 0, 0, 0.08)',
            borderWidth: 1,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            height: 42,
            position: 'relative',
            zIndex: 2,
            ...webTransition(
              'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
            ),
          },
          { backgroundColor: theme.colors['white-label.publish-button.bg.header-bar'], borderRadius: 21 },
        ]}
      >
        <Box flexDirection="row" alignItems="center" gap="spacing.general.x1">
          <IconCirclePlus
            strokeWidth={ICON_STROKE_WIDTH}
            size={23}
            color={theme.colors['white-label.publish-button.icon.header-bar']}
          />
          <Text color="white-label.publish-button.label.primary" fontSize={16} fontWeight="700" lineHeight={20}>
            {t('publishButton')}
          </Text>
          <IconChevronDown
            strokeWidth={ICON_STROKE_WIDTH}
            size={23}
            color={theme.colors['white-label.publish-button.icon.header-bar']}
          />
        </Box>
      </Button>
      {isPromptVisible ? <NewListingPrompt state={newListingPromptState} /> : null}
    </Box>
  );
}
